import { useState, useEffect, useCallback, useRef } from 'react';
import type { ApiResponse } from '@/types';
import { useDataCache } from '@/contexts/DataCacheContext';

export interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  lastFetch: Date | null;
}

export interface UseApiOptions {
  immediate?: boolean;
  refreshInterval?: number;
  retryCount?: number;
  retryDelay?: number;
}

export function useApi<T>(
  apiCall: () => Promise<T>,
  options: UseApiOptions = {}
) {
  const {
    immediate = true,
    refreshInterval,
    retryCount = 0,
    retryDelay = 1000,
  } = options;

  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: false,
    error: null,
    lastFetch: null,
  });

  const intervalRef = useRef<NodeJS.Timeout>();
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const mountedRef = useRef(true);
  // ✅ FIX: Store apiCall in ref to prevent re-execution loops
  const apiCallRef = useRef(apiCall);
  
  // Update ref when apiCall changes (but don't trigger re-execution)
  useEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  const execute = useCallback(async (attempt = 0): Promise<void> => {
    if (!mountedRef.current) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // ✅ FIX: Use ref instead of direct apiCall
      const result = await apiCallRef.current();
      
      if (!mountedRef.current) return;

      setState({
        data: result,
        loading: false,
        error: null,
        lastFetch: new Date(),
      });
    } catch (error) {
      if (!mountedRef.current) return;

      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      if (attempt < retryCount) {
        retryTimeoutRef.current = setTimeout(() => {
          execute(attempt + 1);
        }, retryDelay * Math.pow(2, attempt)); // Exponential backoff
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    }
  }, [retryCount, retryDelay]); // ✅ FIX: Removed apiCall dependency

  const refresh = useCallback(() => {
    execute();
  }, [execute]);

  // DISABLED: No automatic calls - manual refresh only
  useEffect(() => {
    // NEVER auto-execute - causes API storms
    // User must click refresh buttons
  }, []);

  // Auto-refresh interval disabled
  useEffect(() => {
    // Clear any existing intervals immediately
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    ...state,
    execute,
    refresh,
  };
}

// All polling functions now redirect to manual checks
export function usePolling<T>(
  apiCall: () => Promise<T>,
  interval?: number, // Ignored - kept for compatibility
  options: Omit<UseApiOptions, 'refreshInterval'> = {}
) {
  return useManualCheck(apiCall, options);
}

// Health polling redirected to manual for safety
export function useHealthPolling<T>(
  apiCall: () => Promise<T>,
  interval?: number, // Ignored - kept for compatibility
  options: Omit<UseApiOptions, 'refreshInterval'> = {}
) {
  return useManualCheck(apiCall, options);
}

// Manual-only hook (loads data once on mount, then manual refresh only)
export function useManualCheck<T>(
  apiCall: () => Promise<T>,
  options: Omit<UseApiOptions, 'refreshInterval' | 'immediate'> = {}
) {
  return useApi(apiCall, { ...options, immediate: true, refreshInterval: undefined });
}

// Cached manual check - persists data across navigation, NO AUTO CALLS
export function useCachedManualCheck<T>(
  apiCall: () => Promise<T>,
  cacheKey: string,
  options: Omit<UseApiOptions, 'refreshInterval' | 'immediate'> = {}
) {
  const { getCachedData, setCachedData, isCacheValid } = useDataCache();
  const [state, setState] = useState<UseApiState<T>>(() => {
    // Initialize with cached data if available
    const cachedData = getCachedData<T>(cacheKey);
    return {
      data: cachedData || null,
      loading: false,
      error: null,
      lastFetch: cachedData ? new Date() : null,
    };
  });

  const mountedRef = useRef(true);
  // ✅ FIX: Store apiCall in ref to prevent re-execution loops
  const apiCallRef = useRef(apiCall);
  
  // Update ref when apiCall changes (but don't trigger re-execution)
  useEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  const execute = useCallback(async (bypassCache = false): Promise<void> => {
    if (!mountedRef.current) return;

    // Check cache first (unless bypassing)
    if (!bypassCache && isCacheValid(cacheKey)) {
      const cachedData = getCachedData<T>(cacheKey);
      if (cachedData) {
        setState({
          data: cachedData,
          loading: false,
          error: null,
          lastFetch: new Date(),
        });
        return;
      }
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // ✅ FIX: Use ref instead of direct apiCall
      const result = await apiCallRef.current();
      
      if (!mountedRef.current) return;

      // Cache successful response
      setCachedData(cacheKey, result);

      setState({
        data: result,
        loading: false,
        error: null,
        lastFetch: new Date(),
      });
    } catch (error) {
      if (!mountedRef.current) return;

      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [cacheKey, getCachedData, setCachedData, isCacheValid]); // ✅ FIX: Removed apiCall dependency

  const refresh = useCallback(() => {
    execute(true); // Bypass cache on manual refresh
  }, [execute]);

  // Auto-load data if not in cache
  useEffect(() => {
    // Check for cached data and make initial call if none exists
    const cachedData = getCachedData<T>(cacheKey);
    if (!cachedData) {
      execute(false);
    }
  }, [cacheKey, getCachedData, execute]); // Re-run when cacheKey changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    ...state,
    execute,
    refresh,
  };
}

export function useLazyApi<T>(
  apiCall: () => Promise<T>,
  options: Omit<UseApiOptions, 'immediate'> = {}
) {
  return useApi(apiCall, { ...options, immediate: false });
}

// Hook for managing multiple API states
export function useApiStates() {
  const [states, setStates] = useState<Record<string, UseApiState<any>>>({});

  const setApiState = useCallback(<T>(
    key: string,
    update: Partial<UseApiState<T>>
  ) => {
    setStates(prev => ({
      ...prev,
      [key]: { ...prev[key], ...update },
    }));
  }, []);

  const getApiState = useCallback(<T>(key: string): UseApiState<T> => {
    return states[key] || {
      data: null,
      loading: false,
      error: null,
      lastFetch: null,
    };
  }, [states]);

  const resetApiState = useCallback((key: string) => {
    setStates(prev => {
      const newStates = { ...prev };
      delete newStates[key];
      return newStates;
    });
  }, []);

  const clearAllStates = useCallback(() => {
    setStates({});
  }, []);

  return {
    states,
    setApiState,
    getApiState,
    resetApiState,
    clearAllStates,
  };
}

// Hook for managing form submissions with API calls
export function useApiMutation<TData, TVariables = void>(
  apiCall: (variables: TVariables) => Promise<TData>
) {
  const [state, setState] = useState<UseApiState<TData>>({
    data: null,
    loading: false,
    error: null,
    lastFetch: null,
  });

  // ✅ FIX: Store apiCall in ref to prevent re-execution loops
  const apiCallRef = useRef(apiCall);
  
  // Update ref when apiCall changes
  useEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  const mutate = useCallback(async (variables: TVariables): Promise<TData | null> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // ✅ FIX: Use ref instead of direct apiCall
      const result = await apiCallRef.current(variables);
      
      setState({
        data: result,
        loading: false,
        error: null,
        lastFetch: new Date(),
      });

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));

      return null;
    }
  }, []); // ✅ FIX: Removed apiCall dependency

  const reset = useCallback(() => {
    setState({
      data: null,
      loading: false,
      error: null,
      lastFetch: null,
    });
  }, []);

  return {
    ...state,
    mutate,
    reset,
  };
}

export default useApi;
